import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { MonetizationOn, Save } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import TableSkeleton from "../../components/Layout/TableSkeleton";
import Toast from "../../components/Layout/Toast";
import { CountriesList } from "../process/types/processTypes";
import {
  fetchGovReportRates,
  selectInvoices,
  upsertGovReportRate,
} from "./invoiceSlices";
import { SectionCard } from "./components/SectionCard";

export const GovReportRates = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { govReportRates, loading, success, error } =
    useAppSelector(selectInvoices);

  const [country, setCountry] = useState<string>(CountriesList.saudi_arabia);
  const [amount, setAmount] = useState("");

  useEffect(() => {
    dispatch(fetchGovReportRates());
  }, [dispatch]);

  const handleSave = () => {
    dispatch(upsertGovReportRate({ country, amount: Number(amount) }));
  };

  return (
    <>
      <Helmet>
        <title>{`Government Report Rates | ${t("platform")}`}</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <SectionCard
          icon={<MonetizationOn />}
          title="Government Report Rates"
        >
          <Typography color="textSecondary" sx={{ mb: 1 }}>
            Gov-mandated USD amount reported per departed employee, by
            destination country. Snapshotted onto each employee at departure.
          </Typography>
          <Grid container spacing={2} alignItems="center">
          <Grid item md={4} xs={12}>
            <TextField
              id="rateCountry"
              select
              label={t("country")}
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                const existing = govReportRates.find(
                  (r) => r.country === e.target.value,
                );
                setAmount(existing ? String(existing.amount) : "");
              }}
              variant="outlined"
              size="small"
              fullWidth
            >
              {Object.values(CountriesList).map((option) => (
                <MenuItem key={option} value={option}>
                  {te(option)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item md={4} xs={12}>
            <TextField
              id="rateAmount"
              label="Amount (USD)"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
            />
          </Grid>
            <Grid item md={4} xs={12}>
              <Button
                color="secondary"
                variant="contained"
                disabled={loading === "pending" || !(Number(amount) > 0)}
                onClick={handleSave}
              >
                <Save /> {t("save")}
              </Button>
            </Grid>
          </Grid>
        </SectionCard>

        {success && <Toast severity="success">{success.message}</Toast>}
        {error && <Toast severity="error">{error.message}</Toast>}

        <TableContainer
          component={Paper}
          elevation={3}
          sx={{ borderRadius: "8px" }}
        >
          <Typography variant="h6" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            Configured Rates ({govReportRates.length})
          </Typography>
          <Table size="small" aria-label="gov report rates table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>S.No</StyledTableCell>
                <StyledTableCell>{t("country")}</StyledTableCell>
                <StyledTableCell align="right">Amount (USD)</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {loading === "pending" ? (
                <TableSkeleton numRows={5} numColumns={3} />
              ) : (
                govReportRates.map((rate, index) => (
                  <StyledTableRow key={rate.country}>
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell>{te(rate.country)}</StyledTableCell>
                    <StyledTableCell align="right">
                      {rate.amount.toLocaleString()}
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};
